/**
 * Zod i18n bootstrap — overrides the default error map so that
 * ``form.errors`` produced by ``useFormUX(Schema)`` already carry
 * localized messages. Callers can still attach a custom
 * ``{ message: "…" }`` per-rule for cases that need specific copy.
 *
 * Wiring: ``installZodErrorMap("es")`` is invoked on mount AND every
 * time the user toggles the language inside ``LangProvider``. The
 * function is idempotent — it just replaces the active error map.
 *
 * Why a function and not a constant map? Zod's error map signature
 * receives ``(issue, ctx)`` and we want zero-allocation message lookups
 * for the language ACTIVE AT VALIDATION TIME (not at module-load time).
 */
import { z } from "zod";

const MESSAGES = {
  es: {
    invalid_type: ({ expected, received }) =>
      received === "undefined"
        ? "Este campo es obligatorio."
        : `Se esperaba ${expected}, recibido ${received}.`,
    too_small_str: (min) => `Mínimo ${min} caracteres.`,
    too_small_num: (min) => `Debe ser mayor o igual a ${min}.`,
    too_small_required: "Este campo es obligatorio.",
    too_big_str: (max) => `Máximo ${max} caracteres.`,
    too_big_num: (max) => `Debe ser menor o igual a ${max}.`,
    invalid_string_email: "Email inválido.",
    invalid_string_url: "URL inválida.",
    invalid_string_uuid: "UUID inválido.",
    invalid_string_regex: "Formato inválido.",
    invalid_enum_value: ({ options }) =>
      `Debe ser uno de: ${options.join(", ")}.`,
    invalid_date: "Fecha inválida.",
    not_finite: "Número inválido.",
    default: "Valor inválido.",
  },
  en: {
    invalid_type: ({ expected, received }) =>
      received === "undefined"
        ? "This field is required."
        : `Expected ${expected}, got ${received}.`,
    too_small_str: (min) => `Minimum ${min} characters.`,
    too_small_num: (min) => `Must be ≥ ${min}.`,
    too_small_required: "This field is required.",
    too_big_str: (max) => `Maximum ${max} characters.`,
    too_big_num: (max) => `Must be ≤ ${max}.`,
    invalid_string_email: "Invalid email.",
    invalid_string_url: "Invalid URL.",
    invalid_string_uuid: "Invalid UUID.",
    invalid_string_regex: "Invalid format.",
    invalid_enum_value: ({ options }) =>
      `Must be one of: ${options.join(", ")}.`,
    invalid_date: "Invalid date.",
    not_finite: "Invalid number.",
    default: "Invalid value.",
  },
  pt: {
    invalid_type: ({ expected, received }) =>
      received === "undefined"
        ? "Este campo é obrigatório."
        : `Esperado ${expected}, recebido ${received}.`,
    too_small_str: (min) => `Mínimo ${min} caracteres.`,
    too_small_num: (min) => `Deve ser ≥ ${min}.`,
    too_small_required: "Este campo é obrigatório.",
    too_big_str: (max) => `Máximo ${max} caracteres.`,
    too_big_num: (max) => `Deve ser ≤ ${max}.`,
    invalid_string_email: "E-mail inválido.",
    invalid_string_url: "URL inválida.",
    invalid_string_uuid: "UUID inválido.",
    invalid_string_regex: "Formato inválido.",
    invalid_enum_value: ({ options }) =>
      `Deve ser um de: ${options.join(", ")}.`,
    invalid_date: "Data inválida.",
    not_finite: "Número inválido.",
    default: "Valor inválido.",
  },
};

/**
 * Build a Zod ErrorMap for the requested language. We keep the function
 * shape so callers can swap maps cheaply (e.g. when the user toggles
 * the language inside the SPA without a reload).
 *
 * @param {"es"|"en"|"pt"} lang
 * @returns {import("zod").ZodErrorMap}
 */
function buildErrorMap(lang) {
  const M = MESSAGES[lang] || MESSAGES.en;
  return (issue, ctx) => {
    // Honour explicit per-rule messages first — they win over i18n
    // defaults because callers use them for domain-specific copy.
    if (issue?.message && issue.message !== ctx.defaultError) {
      return { message: issue.message };
    }
    switch (issue.code) {
      case z.ZodIssueCode.invalid_type:
        return { message: M.invalid_type(issue) };
      case z.ZodIssueCode.too_small: {
        const isStr = issue.type === "string";
        const isReq = isStr && issue.minimum === 1;
        if (isReq) return { message: M.too_small_required };
        return { message: (isStr ? M.too_small_str : M.too_small_num)(issue.minimum) };
      }
      case z.ZodIssueCode.too_big: {
        const isStr = issue.type === "string";
        return { message: (isStr ? M.too_big_str : M.too_big_num)(issue.maximum) };
      }
      case z.ZodIssueCode.invalid_string:
        if (issue.validation === "email") return { message: M.invalid_string_email };
        if (issue.validation === "url") return { message: M.invalid_string_url };
        if (issue.validation === "uuid") return { message: M.invalid_string_uuid };
        if (issue.validation === "regex") return { message: M.invalid_string_regex };
        return { message: M.invalid_string_regex };
      case z.ZodIssueCode.invalid_enum_value:
        return { message: M.invalid_enum_value(issue) };
      case z.ZodIssueCode.invalid_date:
        return { message: M.invalid_date };
      case z.ZodIssueCode.not_finite:
        return { message: M.not_finite };
      default:
        return { message: ctx.defaultError || M.default };
    }
  };
}

/** Install the Zod error map for the given language globally. */
export function installZodErrorMap(lang = "en") {
  z.setErrorMap(buildErrorMap(lang));
}
